import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ChevronDown, ChevronRight, Loader2, AlertTriangle, FileCheck2, Clock, MinusCircle, BadgeCheck, Quote } from "lucide-react";

const GROUP_LABEL = {
  compliance: "Compliance crosswalk",
  forms: "Forms & pricing package",
  total: "Reconciled total (F4)",
  pricing: "Pricing grounding (honesty)",
  package: "Package linter (KB §0/§6)",
  completeness: "Package completeness (KB §6)",
  submission: "Submission method",
};

// Structural A — render the send gate as 3 tiers: AUTO-DONE (green) / OFFICER 1-CLICK / GENUINE EXTERNAL.
const TierBreakdown = ({ tiers }) => {
  if (!tiers) return null;
  const { auto_done = [], officer_one_click = [], genuine_external = [],
          post_award_tracked = [], not_applicable = [] } = tiers;
  return (
    <div className="space-y-3" data-testid="send-gate-tiers">
      {genuine_external.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-100/50 p-3" data-testid="tier-genuine-external">
          <div className="text-[11px] font-bold text-red-800 uppercase tracking-wide mb-1.5">
            Needs a human / external action ({genuine_external.length})
          </div>
          <ul className="space-y-1.5">
            {genuine_external.map((it, i) => (
              <li key={i} className="text-[12px] text-red-900 flex gap-1.5">
                <ShieldAlert className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                <span className="min-w-0"><b>{it.label}</b> — {it.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {officer_one_click.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="tier-officer-one-click">
          <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wide mb-1.5">
            Your one-click commit ({officer_one_click.length})
          </div>
          <ul className="space-y-1.5">
            {officer_one_click.map((it, i) => (
              <li key={i} className="text-[12px] text-amber-900 flex gap-1.5">
                <FileCheck2 className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                <span className="min-w-0"><b>{it.label}</b> — {it.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {auto_done.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3" data-testid="tier-auto-done">
          <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide mb-1.5">
            Done automatically ({auto_done.length})
          </div>
          <ul className="space-y-1.5">
            {auto_done.map((it, i) => (
              <li key={i} className="text-[12px] text-emerald-900 flex gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="min-w-0"><b>{it.label}</b>{it.detail ? ` — ${it.detail}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {post_award_tracked.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-3" data-testid="tier-post-award">
          <div className="text-[11px] font-bold text-sky-800 uppercase tracking-wide mb-1.5">
            Post-award / performance — tracked, not blocking ({post_award_tracked.length})
          </div>
          <ul className="space-y-1.5">
            {post_award_tracked.map((it, i) => (
              <li key={i} className="text-[12px] text-sky-900 flex gap-1.5">
                <Clock className="h-3.5 w-3.5 text-sky-500 shrink-0 mt-0.5" />
                <span className="min-w-0">
                  <b>{it.status === "acknowledged" ? "Acknowledged in proposal" : "Tracked to-do"}</b>
                  {it.detail ? ` — ${it.detail}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {not_applicable.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/50 p-3" data-testid="tier-na">
          <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
            Not applicable to this bid ({not_applicable.length})
          </div>
          <ul className="space-y-1.5">
            {not_applicable.map((it, i) => (
              <li key={i} className="text-[12px] text-foreground/80 flex gap-1.5">
                <MinusCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <span className="min-w-0">{it.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// One-line go / no-go banner in the deal cockpit: RED (with the exact pre-send blockers) until every
// hard gate is green, then GREEN. A total mismatch (F4) is shown diagnostically — the conflicting numbers
// side by side + where each came from — and is NOT overridable.
export const SendReadinessBanner = ({ dealId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveRes, setResolveRes] = useState(null);
  const [regenning, setRegenning] = useState(false);
  const [regenDiff, setRegenDiff] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/bids/${dealId}/send-readiness`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dealId]);

  // One-click TRIGGER for the GUARDED gap-resolver (same backend as the Compliance tab). It only flips a
  // requirement to green when there is REAL, traceable, cited coverage; genuinely-missing items STAY red.
  // This is a convenience entry point — it never lowers the gate.
  const resolveGaps = async (e) => {
    e.stopPropagation();
    setResolving(true); setResolveRes(null);
    try {
      await api.post(`/bids/${dealId}/resolve-compliance-gaps`);
      const t0 = Date.now();
      const poll = async () => {
        const { data: r } = await api.get(`/bids/${dealId}/gap-resolution`);
        if (r.status === "done") {
          setResolveRes(r); setResolving(false);
          toast.success(`${r.resolved} covered (cited & validated) · ${r.still_missing} genuinely missing (stay flagged)`);
          load(); window.dispatchEvent(new Event("ironclad-refresh"));
          return;
        }
        if (r.status === "error") { setResolving(false); toast.error("Gap resolution failed."); return; }
        if (Date.now() - t0 > 180000) { setResolving(false); toast.error("Gap resolution timed out."); return; }
        setTimeout(poll, 2500);
      };
      poll();
    } catch (err) {
      setResolving(false);
      toast.error(err?.response?.data?.detail || "Gap resolution failed.");
    }
  };

  // Dir 67 — "Regenerate clean": non-destructively re-draft the narrative so a package-linter blocker clears
  // because the content is ACTUALLY clean. Pricing/forms/signature untouched. The gate re-runs and stays authority.
  const regenerateClean = async (e) => {
    e.stopPropagation();
    setRegenning(true);
    try {
      const { data: r0 } = await api.post(`/bids/${dealId}/regenerate-clean`);
      if (r0.status === "clean") { setRegenning(false); toast.message("Package narrative is already clean."); load(); return; }
      const t0 = Date.now();
      const poll = async () => {
        try {
          const { data: r } = await api.get(`/bids/${dealId}/redraft-status`);
          if (r.status === "done") {
            setRegenning(false);
            setRegenDiff({ clean: r.clean, remaining: r.remaining || [], diff: r.diff || [] });
            if (r.clean) toast.success("Regenerated clean — package-linter blocker cleared.");
            else toast.message(`Re-drafted, but the linter still flags: ${(r.remaining || []).join(", ")}. Edit the narrative or re-run.`);
            load(); window.dispatchEvent(new Event("ironclad-refresh"));
            return;
          }
          if (r.status === "error") { setRegenning(false); toast.error(r.error || "Regenerate failed."); return; }
        } catch { /* transient */ }
        if (Date.now() - t0 > 300000) { setRegenning(false); load(); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setRegenning(false);
      toast.error(err?.response?.data?.detail || "Regenerate failed.");
    }
  };

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("ironclad-refresh", onRefresh);
    return () => window.removeEventListener("ironclad-refresh", onRefresh);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="mb-5 rounded-xl border border-zinc-200 bg-white px-4 py-3 flex items-center gap-2 text-sm text-zinc-500" data-testid="send-readiness-banner">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking send readiness…
      </div>
    );
  }
  if (!data) return null;

  const ready = data.ready;
  const groups = (data.groups || []).filter((g) => (g.blockers || []).length > 0);
  const mismatches = data.total_mismatches || [];
  const notices = data.notices || [];
  const wd = data.wage_determination;
  const commitments = data.commitments || [];
  const wdTone = wd?.status === "grounded" ? "bg-emerald-100 text-emerald-700"
    : wd?.status ? "bg-amber-100 text-amber-700" : "";

  return (
    <div
      data-testid="send-readiness-banner"
      className="mb-5 rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        data-testid="send-readiness-toggle"
      >
        {ready ? (
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
        ) : (
          <ShieldAlert className="h-5 w-5 text-zinc-400 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2" data-testid="send-readiness-status">
            {ready ? (
              "Ready to send — all pre-send gates are green."
            ) : (
              <>
                Send readiness
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200"
                  data-testid="send-readiness-blocker-count">
                  {data.count} to resolve
                </span>
              </>
            )}
          </div>
          {!ready && (
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
              {groups.map((g) => `${GROUP_LABEL[g.key] || g.key} (${g.blockers.length})`).join(" · ")}
            </div>
          )}
          {ready && (
            <div className="text-[11px] text-emerald-700/80 mt-0.5">
              Reconciled total {data.canonical_total?.display || "—"} · submission via {data.submission_method}.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {commitments.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setOpen(true); setCommitOpen(true); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setOpen(true); setCommitOpen(true); } }}
              title="Requirements covered by commitment language in the proposal — not missing items"
              data-testid="send-readiness-commitments-chip"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200 transition-colors cursor-pointer"
            >
              <BadgeCheck className="h-3 w-3" /> {commitments.length} commitment{commitments.length === 1 ? "" : "s"}
            </span>
          )}
          {wd?.status && (
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${wdTone}`} data-testid="send-readiness-wage-basis"
              title={wd.note}>
              {wd.status === "grounded" ? `Wage: ${wd.wd_number || "grounded"}` : "Wage: not grounded"}
            </span>
          )}
          {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-200/70 bg-card" data-testid="send-readiness-detail">
          {/* Dir 66 — GC-doctrine COMMITMENTS: requirements covered by commitment language in the proposal.
              Deliberately green/neutral — these are NOT blockers and NOT missing gaps. Cockpit-only. */}
          {commitments.length > 0 && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50/80 overflow-hidden" data-testid="send-readiness-commitments">
              <button
                type="button"
                onClick={() => setCommitOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                data-testid="send-readiness-commitments-toggle"
              >
                <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-bold text-emerald-800 uppercase tracking-wide">
                    Commitments ({commitments.length}) — covered by proposal language
                  </div>
                  <div className="text-[11px] text-emerald-700/80">
                    General-contractor commitments (e.g. certified leads assigned/subcontracted at award). Not missing items — not a blocker.
                  </div>
                </div>
                {commitOpen ? <ChevronDown className="h-4 w-4 text-emerald-500" /> : <ChevronRight className="h-4 w-4 text-emerald-500" />}
              </button>
              {commitOpen && (
                <ul className="px-3 pb-3 space-y-2.5">
                  {commitments.map((c, i) => (
                    <li key={i} className="rounded-md border border-emerald-200 bg-white p-2.5" data-testid={`commitment-item-${i}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
                          {c.cert || "commitment"}
                        </span>
                      </div>
                      <div className="text-[12px] text-zinc-700">{c.note}</div>
                      {c.evidence ? (
                        <div className="mt-2 flex gap-1.5 rounded bg-emerald-50 border border-emerald-100 px-2 py-1.5" data-testid={`commitment-evidence-${i}`}>
                          <Quote className="h-3 w-3 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="text-[11px] italic text-emerald-900/90 min-w-0">“{c.evidence}”</span>
                        </div>
                      ) : (
                        <div className="mt-2 text-[11px] text-amber-700" data-testid={`commitment-evidence-${i}`}>
                          Commitment language not found in the current draft — re-draft Stage 5 so the covering language is written.
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* One-click GUARDED gap-resolver (only flips genuinely-covered, cited items; missing stay red) */}
          {!ready && (groups.find((g) => g.key === "compliance")?.blockers?.length > 0) && (
            <div className="flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2 bg-zinc-50">
              <div className="text-[11px] text-zinc-600 min-w-0">
                Anvil auto-closes ONLY gaps that are genuinely covered (real, cited proposal text). Truly missing
                items stay flagged — never gamed to clear the banner.
              </div>
              <button onClick={resolveGaps} disabled={resolving} data-testid="banner-resolve-gaps-btn"
                className="shrink-0 text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1">
                {resolving ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileCheck2 className="h-3 w-3" />}
                {resolving ? "Resolving…" : "Resolve remaining gaps"}
              </button>
            </div>
          )}
          {resolveRes && (
            <div className="border border-blue-200 bg-blue-50 rounded-md px-3 py-2 text-[12px] text-blue-900" data-testid="banner-resolve-gaps-result">
              Classified {resolveRes.classified} · <b>{resolveRes.resolved} covered</b> (cited &amp; validated) ·
              {" "}<b>{resolveRes.still_missing} genuinely missing</b> (stay flagged, not auto-closed).
            </div>
          )}

          {/* Wage-basis (F3) status */}
          {wd?.status && (
            <div className="text-[11px] text-zinc-600" data-testid="send-readiness-wage-note">
              <span className="font-semibold text-zinc-700 uppercase tracking-wide">Wage basis · </span>{wd.note}
              {wd.source ? ` (${wd.source}${wd.retrieved_at ? `, retrieved ${wd.retrieved_at}` : ""})` : ""}
            </div>
          )}

          {/* F4 diagnostic — conflicting totals side by side */}
          {!ready && mismatches.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-100/60 p-3" data-testid="send-readiness-total-mismatch">
              <div className="flex items-center gap-1.5 text-[12px] font-bold text-red-800 uppercase tracking-wide">
                <AlertTriangle className="h-3.5 w-3.5" /> Total mismatch — hard block (not overridable)
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                <div className="rounded-md bg-white border border-zinc-200 px-2.5 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">Source of truth · cost model</div>
                  <div className="font-mono font-bold text-zinc-900">{data.canonical_total?.display}</div>
                </div>
                {mismatches.map((m, i) => (
                  <div key={i} className="rounded-md bg-white border border-red-200 px-2.5 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-red-400">{m.surface}</div>
                    <div className="font-mono font-bold text-red-700">{m.found_display}</div>
                    <div className="text-[10px] text-zinc-500 truncate" title={m.where}>{m.where}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-red-700/90">
                Re-run Pricing (Stage 4), then re-draft (Stage 5) / re-detect submission (Stage 7) so the one
                reconciled total propagates from the cost model to every surface.
              </div>
            </div>
          )}

          {!ready && groups.map((g) => (
            <div key={g.key} data-testid={`send-readiness-group-${g.key}`}>
              <div className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wide mb-1">
                {GROUP_LABEL[g.key] || g.key} <span className="text-red-500">({g.blockers.length})</span>
              </div>
              <ul className="space-y-1">
                {g.blockers.slice(0, 8).map((b, i) => (
                  <li key={i} className="text-[12px] text-zinc-600 flex gap-1.5">
                    <span className="text-red-400 mt-0.5">•</span>
                    <span className="min-w-0">{b}</span>
                  </li>
                ))}
                {g.blockers.length > 8 && (
                  <li className="text-[11px] text-zinc-400 pl-3">+{g.blockers.length - 8} more…</li>
                )}
              </ul>
              {g.key === "package" && (
                <div className="mt-2 flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2 bg-zinc-50">
                  <div className="text-[11px] text-zinc-600 min-w-0">
                    Anvil re-drafts ONLY the narrative (non-destructive — pricing, forms &amp; signature untouched)
                    so it&apos;s actually clean. The linter re-runs and stays the authority.
                  </div>
                  <button onClick={regenerateClean} disabled={regenning} data-testid="banner-regenerate-clean-btn"
                    className="shrink-0 text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1">
                    {regenning ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileCheck2 className="h-3 w-3" />}
                    {regenning ? "Regenerating…" : "Regenerate clean"}
                  </button>
                </div>
              )}
              {g.key === "package" && regenDiff && (
                <div className="mt-2 rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="regenerate-clean-diff">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-50 border-b border-zinc-200">
                    <span className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wide">
                      What Anvil rewrote {regenDiff.clean ? "· linter now clean" : `· still flags: ${regenDiff.remaining.join(", ")}`}
                    </span>
                    <span className="text-[10px] text-zinc-400">cockpit-only · not on the PDF</span>
                  </div>
                  {regenDiff.diff.length === 0 ? (
                    <div className="px-3 py-2 text-[11px] text-zinc-500">No line-level narrative changes detected.</div>
                  ) : (
                    <ul className="max-h-56 overflow-auto divide-y divide-zinc-100 font-mono text-[11px]">
                      {regenDiff.diff.map((d, i) => (
                        <li key={i} className={`px-3 py-1 flex gap-2 ${d.op === "add" ? "bg-green-50" : d.op === "del" ? "bg-red-50" : ""}`}>
                          <span className={`shrink-0 font-bold ${d.op === "add" ? "text-green-600" : d.op === "del" ? "text-red-500" : "text-zinc-400"}`}>
                            {d.op === "add" ? "+" : d.op === "del" ? "−" : "…"}
                          </span>
                          <span className={`min-w-0 ${d.op === "del" ? "text-red-700 line-through/50" : "text-zinc-700"}`}>{d.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Structural A — the gate in 3 tiers */}
          {data.tiers && <TierBreakdown tiers={data.tiers} />}

          {/* F5 — informational notices are tracked here (dates the team needs) but do NOT block sending */}
          {notices.length > 0 && (
            <div data-testid="send-readiness-notices">
              <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">
                Tracked dates &amp; notices <span className="text-zinc-400">({notices.length}, non-blocking)</span>
              </div>
              <ul className="space-y-1">
                {notices.slice(0, 6).map((n, i) => (
                  <li key={i} className="text-[12px] text-zinc-500 flex gap-1.5">
                    <span className="text-zinc-300 mt-0.5">◦</span>
                    <span className="min-w-0">{n.text}</span>
                  </li>
                ))}
                {notices.length > 6 && (
                  <li className="text-[11px] text-zinc-400 pl-3">+{notices.length - 6} more tracked notices…</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SendReadinessBanner;
